import { useMergeRefs } from "@floating-ui/react";
import { mergeProps } from "@react-aria/utils";
import cx from "classnames";
import { KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useFocus, useKeyboard, usePress } from "react-aria";

import Box from "ds/components/Box";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import { TreeItem, TreeItemContent, TreeItemToggle } from "ds/components/Tree";
import useTypedContext from "hooks/useTypedContext";

import { TreeListContext } from "../Context";
import { TreeListHighlightLabel } from "../HighlightLabel";
import { BaseTreeListItemProps } from "../types";
import styles from "./styles.module.css";

const TreeListItem = <T extends object>({
  id,
  name,
  isExpanded,
  isDisabled,
  searchQuery,
  postfix,
  selected,
  innerRef,
  hasFocus,
  ariaLevel,
  onToggle,
  onFocus,
  onBlur,
  onKeyDown,
  onClick,
}: BaseTreeListItemProps<T>) => {
  const { isVirtualized } = useTypedContext(TreeListContext);
  const ref = useRef<HTMLDivElement>(null);
  const mergedRef = useMergeRefs([innerRef, ref]);
  const [isFocusHidden, setIsFocusHidden] = useState(false);

  const handleToggleIconPressEnd = useCallback(() => {
    setIsFocusHidden(true);
    onFocus?.();
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setIsFocusHidden(false);
    onBlur?.();
  }, [onBlur]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => onKeyDown?.(e, id), [id, onKeyDown]);

  const handleKeyup = useCallback(() => {
    setIsFocusHidden(false);
  }, []);

  const { focusProps } = useFocus({
    onBlur: handleBlur,
  });

  const { pressProps: wrapperPressProps } = usePress({
    onPress: onClick,
    isDisabled,
  });

  const { keyboardProps } = useKeyboard({
    onKeyDown: handleKeyDown,
    onKeyUp: handleKeyup,
  });

  useEffect(() => {
    const elementRef = ref.current;
    if (hasFocus && elementRef) {
      elementRef.focus();

      if (!isFocusHidden && !isVirtualized) {
        elementRef.scrollIntoView({ block: "center", inline: "nearest" });
      }
    }
  }, [hasFocus, isFocusHidden, isVirtualized]);

  return (
    <TreeItem
      {...mergeProps(focusProps, keyboardProps, wrapperPressProps)}
      isSelected={selected}
      isDisabled={isDisabled}
      isFocusHidden={isFocusHidden}
      isInteractive={!!onClick}
      isIndented={!onToggle}
      ref={mergedRef}
      tabIndex={-1}
      role="treeitem"
      aria-selected={selected}
      aria-disabled={isDisabled}
      aria-expanded={isExpanded}
      aria-level={ariaLevel}
    >
      {!!onToggle && (
        <TreeItemToggle
          isExpanded={isExpanded}
          onPress={onToggle}
          onPressEnd={handleToggleIconPressEnd}
          excludeFromTabOrder
        />
      )}

      <TreeItemContent>
        <TextEllipsis tooltip={name} tooltipPlacement="top-end" delay={400}>
          {(ellipsisProps) => (
            <Typography
              {...ellipsisProps}
              variant="p-body3"
              tag="span"
              className={cx(
                ellipsisProps.className,
                cx(styles.name, isDisabled && styles.nameDisabled)
              )}
            >
              {isDisabled ? name : <TreeListHighlightLabel query={searchQuery} label={name} />}
            </Typography>
          )}
        </TextEllipsis>
        {postfix && (
          <Box className={cx(styles.postfix, isDisabled && styles.postfixDisabled)} shrink="0">
            {postfix}
          </Box>
        )}
      </TreeItemContent>
    </TreeItem>
  );
};

TreeListItem.displayName = "DS.TreeList.Item";

export default TreeListItem;
